import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { QuizQuestion, UserQuestionProgress, QuestionWithProgress, SPACED_REPETITION_INTERVALS } from '@/types/quiz.types';
import { toast } from 'sonner';
import { useRecordActivity } from '@/hooks/useActivityTracker';

export function useDueQuestions() {
  const { user } = useAuth();
  const today = new Date().toISOString().split('T')[0];

  return useQuery({
    queryKey: ['due-questions', user?.id, today],
    queryFn: async () => {
      if (!user?.id) return { questions: [], noActiveQuizzes: false };

      const { data: activeQuizzes, error: activeError } = await (supabase
        .from('quiz_usage' as any)
        .select('id')
        .eq('user_id', user.id)
        .eq('is_active', true)) as { data: { id: string }[] | null; error: any };

      if (activeError) throw activeError;
      
      const activeQuizUsageIds = activeQuizzes?.map(q => q.id) || [];
      
      if (activeQuizUsageIds.length === 0) {
        // Check if user has ANY quizzes (all inactive) vs none at all
        const { count } = await (supabase
          .from('quiz_usage' as any)
          .select('id', { count: 'exact', head: true })
          .eq('user_id', user.id)) as { count: number | null };
        
        return { questions: [], noActiveQuizzes: (count ?? 0) > 0 };
      }

      const { data: questions, error: questionsError } = await (supabase
        .from('quiz_questions' as any)
        .select('*')
        .eq('user_id', user.id)
        .in('quiz_usage_id', activeQuizUsageIds)) as { data: QuizQuestion[] | null; error: any };

      if (questionsError) throw questionsError;
      if (!questions?.length) return { questions: [], noActiveQuizzes: false };

      const questionIds = questions.map(q => q.id);
      const { data: progressData, error: progressError } = await (supabase
        .from('user_question_progress' as any)
        .select('*')
        .eq('user_id', user.id)
        .in('question_id', questionIds)) as { data: UserQuestionProgress[] | null; error: any };

      if (progressError) throw progressError;

      const progressMap = new Map(progressData?.map(p => [p.question_id, p]) || []);

      const dueQuestions = questions.filter(q => {
        const progress = progressMap.get(q.id);
        if (!progress) return true;
        return progress.next_due_date <= today;
      });

      return {
        questions: dueQuestions.map(q => ({
          ...q,
          progress: progressMap.get(q.id),
        })),
        noActiveQuizzes: false,
      };
    },
    enabled: !!user?.id,
  });
}

export function useAnswerQuestion() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      questionId, 
      isCorrect,
      userAnswer,
    }: { 
      questionId: string; 
      isCorrect: boolean;
      userAnswer: string;
    }) => {
      if (!user?.id) throw new Error('Not authenticated');

      const today = new Date().toISOString().split('T')[0];

      // Get current progress
      const { data: existingProgress } = await (supabase
        .from('user_question_progress' as any)
        .select('*')
        .eq('user_id', user.id)
        .eq('question_id', questionId)
        .maybeSingle()) as { data: UserQuestionProgress | null; error: any };

      let newLevel: number;
      let timesCorrect: number;
      let timesIncorrect: number;

      if (existingProgress) {
        if (isCorrect) {
          newLevel = Math.min(existingProgress.level + 1, SPACED_REPETITION_INTERVALS.length - 1);
          timesCorrect = existingProgress.times_correct + 1;
          timesIncorrect = existingProgress.times_incorrect;
        } else {
          newLevel = 0; // Reset to level 0 on incorrect
          timesCorrect = existingProgress.times_correct;
          timesIncorrect = existingProgress.times_incorrect + 1;
        }
      } else {
        newLevel = isCorrect ? 1 : 0;
        timesCorrect = isCorrect ? 1 : 0;
        timesIncorrect = isCorrect ? 0 : 1;
      }

      // For incorrect answers at level 0, keep due today so user must get it right
      const intervalDays = isCorrect || newLevel > 0 ? SPACED_REPETITION_INTERVALS[newLevel] : 0;
      const nextDueDate = new Date();
      nextDueDate.setDate(nextDueDate.getDate() + intervalDays);
      const nextDueDateStr = nextDueDate.toISOString().split('T')[0];

      if (existingProgress) {
        const { error } = await (supabase
          .from('user_question_progress' as any)
          .update({
            level: newLevel,
            next_due_date: nextDueDateStr,
            last_reviewed_at: new Date().toISOString(),
            times_correct: timesCorrect,
            times_incorrect: timesIncorrect,
            updated_at: new Date().toISOString(),
          })
          .eq('id', existingProgress.id)) as { error: any };

        if (error) throw error;
      } else {
        const { error } = await (supabase
          .from('user_question_progress' as any)
          .insert({
            user_id: user.id,
            question_id: questionId,
            level: newLevel,
            next_due_date: nextDueDateStr,
            last_reviewed_at: new Date().toISOString(),
            times_correct: timesCorrect,
            times_incorrect: timesIncorrect,
          })) as { error: any };

        if (error) throw error;
      }

      return { newLevel, nextDueDate: nextDueDateStr, isCorrect };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['due-questions'] });
    },
    onError: (error) => {
      console.error('Failed to save answer:', error);
      toast.error('Failed to save your answer');
    },
  });
}

export function usePracticeSession() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [sessionStats, setSessionStats] = useState({ correct: 0, incorrect: 0 });
  // Snapshot the questions at session start to prevent mid-session changes
  const [sessionQuestions, setSessionQuestions] = useState<QuestionWithProgress[] | null>(null);

  const { data: dueData, isLoading, error } = useDueQuestions();
  const answerMutation = useAnswerQuestion();
  const recordActivity = useRecordActivity();

  const dueQuestions = dueData?.questions;
  const noActiveQuizzes = dueData?.noActiveQuizzes ?? false;

  // Snapshot questions once loaded (only if no session is active)
  if (dueQuestions && dueQuestions.length > 0 && sessionQuestions === null) {
    setSessionQuestions([...dueQuestions]);
  }

  const questions = sessionQuestions ?? [];
  const currentQuestion = questions[currentIndex];
  const totalQuestions = questions.length;
  const isComplete = currentIndex >= totalQuestions && totalQuestions > 0;

  const handleSelectAnswer = (index: number) => {
    if (showResult) return;
    setSelectedAnswer(index);
  };

  const handleSubmitAnswer = async () => {
    if (selectedAnswer === null || !currentQuestion) return;

    const correctIndex = currentQuestion.options.indexOf(currentQuestion.correct_answer);
    const isCorrect = selectedAnswer === correctIndex;
    setShowResult(true);

    setSessionStats(prev => ({
      correct: prev.correct + (isCorrect ? 1 : 0),
      incorrect: prev.incorrect + (isCorrect ? 0 : 1),
    }));

    await answerMutation.mutateAsync({
      questionId: currentQuestion.id,
      isCorrect,
      userAnswer: currentQuestion.options[selectedAnswer],
    });

    recordActivity();
  };

  const handleNextQuestion = () => {
    setSelectedAnswer(null);
    setShowResult(false);
    setCurrentIndex(prev => prev + 1);
  };

  const resetSession = () => {
    setCurrentIndex(0);
    setSelectedAnswer(null);
    setShowResult(false);
    setSessionStats({ correct: 0, incorrect: 0 });
    setSessionQuestions(null); // Allow re-snapshot on next load
  };

  return {
    currentQuestion,
    currentIndex,
    totalQuestions,
    selectedAnswer,
    showResult,
    sessionStats,
    isLoading: isLoading && sessionQuestions === null,
    error,
    isComplete,
    isSubmitting: answerMutation.isPending,
    noActiveQuizzes,
    handleSelectAnswer,
    handleSubmitAnswer,
    handleNextQuestion,
    resetSession,
  };
}
